import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import {
  IconSingle,
  IconTwoCols,
  IconTwoRows,
  IconThreeCols,
  IconThreeRows,
  IconThreeTopSplit,
  IconThreeBottomSplit,
  IconFourGrid,
  IconFourCols,
  IconFourLeftMain,
  IconFourTopMain,
  getLayoutIcon,
} from "./LayoutIcons";

const LAYOUT_GROUPS = [
  {
    count: 1,
    options: [{ id: "1", title: "Single Chart", icon: IconSingle, slots: 1 }],
  },
  {
    count: 2,
    options: [
      { id: "2-cols", title: "2 Columns (Side by Side)", icon: IconTwoCols, slots: 2 },
      { id: "2-rows", title: "2 Rows (Stacked)", icon: IconTwoRows, slots: 2 },
    ],
  },
  {
    count: 3,
    options: [
      { id: "3-cols", title: "3 Columns", icon: IconThreeCols, slots: 3 },
      { id: "3-rows", title: "3 Rows", icon: IconThreeRows, slots: 3 },
      { id: "3-top-split", title: "1 Top / 2 Bottom", icon: IconThreeTopSplit, slots: 3 },
      { id: "3-bottom-split", title: "2 Top / 1 Bottom", icon: IconThreeBottomSplit, slots: 3 },
    ],
  },
  {
    count: 4,
    options: [
      { id: "4-grid", title: "2x2 Grid", icon: IconFourGrid, slots: 4 },
      { id: "4-cols", title: "4 Columns", icon: IconFourCols, slots: 4 },
      { id: "4-left-main", title: "1 Left / 3 Right", icon: IconFourLeftMain, slots: 4 },
      { id: "4-top-main", title: "1 Top / 3 Bottom", icon: IconFourTopMain, slots: 4 },
    ],
  },
];

const OptionMultiChartSelector = ({ currentLayout = "1", onSelectLayout, btnColor: propBtnColor }) => {
  const reduxBtnColor = useSelector((state) => state?.btncolor);
  const themeBtnColor = propBtnColor || reduxBtnColor || "#1976d2";
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        open &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const handleSelect = (id) => {
    if (onSelectLayout) {
      onSelectLayout(id);
    }
    setOpen(false);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Trigger Button */}
      <button
        ref={btnRef}
        type="button"
        title="Select Multi-Chart Layout"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 8px",
          fontSize: "12px",
          fontWeight: 600,
          color: open ? themeBtnColor : "var(--text-color, #e0e0e0)",
          backgroundColor: open ? alpha(themeBtnColor, 0.15) : "transparent",
          border: `1px solid ${open ? alpha(themeBtnColor, 0.5) : "rgba(125,125,125,0.2)"}`,
          borderRadius: "6px",
          cursor: "pointer",
          outline: "none",
          transition: "all 0.15s ease",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {getLayoutIcon(currentLayout, true, open ? themeBtnColor : "currentColor")}
        </span>
        <span style={{ fontSize: "11px", opacity: 0.9 }}>Layout</span>
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
            opacity: 0.7,
          }}
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Popover Menu */}
      {open && (
        <div
          ref={menuRef}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 9999,
            minWidth: "260px",
            backgroundColor: "var(--body-color-less, #181a20)",
            border: "1px solid rgba(125,125,125,0.22)",
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.55)",
            padding: "10px 12px",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: "var(--text-color, #e0e0e0)",
              opacity: 0.6,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: "8px",
              paddingBottom: "4px",
              borderBottom: "1px solid rgba(125,125,125,0.12)",
            }}
          >
            Options Multi-Chart Layouts
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {LAYOUT_GROUPS.map((group) => (
              <div key={group.count}>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "var(--text-color, #888)",
                    opacity: 0.5,
                    marginBottom: "4px",
                  }}
                >
                  {group.count === 1 ? "Single" : `${group.count} Charts`}
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {group.options.map((opt) => {
                    const isSelected = currentLayout === opt.id;
                    const IconComp = opt.icon;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        title={opt.title}
                        onClick={() => handleSelect(opt.id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "6px 8px",
                          borderRadius: "5px",
                          border: isSelected
                            ? `1.5px solid ${themeBtnColor}`
                            : "1px solid rgba(125,125,125,0.2)",
                          backgroundColor: isSelected
                            ? alpha(themeBtnColor, 0.2)
                            : "rgba(125,125,125,0.06)",
                          cursor: "pointer",
                          outline: "none",
                          transition: "all 0.12s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = alpha(themeBtnColor, 0.4);
                            e.currentTarget.style.backgroundColor = alpha(themeBtnColor, 0.1);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = "rgba(125,125,125,0.2)";
                            e.currentTarget.style.backgroundColor = "rgba(125,125,125,0.06)";
                          }
                        }}
                      >
                        <IconComp
                          active={isSelected}
                          color={isSelected ? themeBtnColor : "var(--text-color, #b0b0b0)"}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionMultiChartSelector;
